import React, { useId } from 'react';
import {
  Save,
  Edit3,
  Lightbulb,
  Rocket,
  Award,
  TrendingUp,
  Scale,
  AlertTriangle,
  PartyPopper,
} from 'lucide-react';

import EditableInput from './common/EditableInput';
import AffiliateCard from './common/AffiliateCard';
import { ARC_PRODUCTS } from '../config/affiliateLinks';
import ChartDefs from './charts/ChartDefs';
import useChartTheme from './charts/useChartTheme';
import { makeChartIds, lighten } from './charts/chartTokens';

/**
 * GoalsTab – smart financial goals with auto-calculations.
 *
 * Props
 *  ─ goals:        Goal[]
 *  ─ editMode:     boolean
 *  ─ onToggleEdit: () => void
 *  ─ updateGoal(index, field, value)
 *  ─ investments:  Investment[] - for calculating emergency fund current
 *  ─ totalInvestments: number - for FIRE calculations
 *  ─ totalDebts:   number - for FIRE calculations
 *  ─ expenseData:  ExpenseRow[] - for calculating expense averages
 */
const GoalsTab = ({
  goals,
  editMode,
  onToggleEdit,
  updateGoal,
  investments = [],
  totalInvestments = 0,
  totalDebts = 0,
  expenseData = [],
  wallets = []
}) => {
  // On-chain crypto holdings — uses manual override if set, else last fetched value
  const totalCryptoHoldings = wallets.reduce(
    (s, w) =>
      s +
      (typeof w.manualValue === "number" && !isNaN(w.manualValue)
        ? w.manualValue
        : w.lastValue || 0),
    0
  );
  // Helper functions
  const calculateExpenseAverage = (months = 6) => {
    if (!expenseData.length) return 0;

    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth(); // 0-11

    // Filter to only include months that have occurred (current month and earlier)
    const pastAndCurrentMonths = expenseData.filter(row => {
      // Parse the month string (assuming format like "Jan 2024", "February 2024", etc.)
      const monthParts = row.month.split(' ');
      let monthName, year;

      if (monthParts.length === 2) {
        [monthName, year] = monthParts;
      } else {
        // If no year specified, assume current year
        monthName = monthParts[0];
        year = currentYear;
      }

      const yearNum = parseInt(year);
      const monthIndex = new Date(`${monthName} 1, ${yearNum}`).getMonth();

      // Include if it's a past year, or same year but past/current month
      return yearNum < currentYear || (yearNum === currentYear && monthIndex <= currentMonth);
    });

    // Get the last N months from the filtered data
    const recentExpenses = pastAndCurrentMonths.slice(-months);
    const totalExpenses = recentExpenses.reduce((sum, row) => sum + (row.total || 0), 0);
    return recentExpenses.length > 0 ? totalExpenses / recentExpenses.length : 0;
  };

  const getEmergencyFundCurrent = () => {
    const emergencyInvestments = investments.filter(inv => inv.type === 'Emergency Fund');
    return emergencyInvestments.reduce((sum, inv) => sum + (inv.amount || 0), 0);
  };

  const getFireCurrent = () => {
    // FIRE progress = manual investments + on-chain wallets − debts
    return totalInvestments + totalCryptoHoldings - totalDebts;
  };

  const formatCurrency = (value) => {
    return `$${(value || 0).toLocaleString()}`;
  };

  // Enhanced goals with auto-calculations
  const enhancedGoals = goals.map((goal, index) => {
    let enhancedGoal = { ...goal };

    switch (goal.name) {
      case 'FIRE Target':
        enhancedGoal.current = getFireCurrent();
        enhancedGoal.progress = enhancedGoal.target ? (enhancedGoal.current / enhancedGoal.target) * 100 : 0;
        break;

      case 'Emergency Fund':
        const avgExpenses6months = calculateExpenseAverage(6);
        const avgExpenses3months = calculateExpenseAverage(3);

        enhancedGoal.current = getEmergencyFundCurrent();
        enhancedGoal.target = avgExpenses6months * 6; // 6 months of expenses
        enhancedGoal.progress = enhancedGoal.target ? (enhancedGoal.current / enhancedGoal.target) * 100 : 0;
        enhancedGoal.avgExpenses3months = avgExpenses3months;
        enhancedGoal.avgExpenses6months = avgExpenses6months;
        break;

      default:
        // For other goals, keep original values but recalculate progress
        enhancedGoal.progress = enhancedGoal.target ? (enhancedGoal.current / enhancedGoal.target) * 100 : 0;
        break;
    }

    return enhancedGoal;
  });

  // Filter out Investment Target and add Emergency Fund + Total FIRE Amount if not exists
  let filteredGoals = enhancedGoals.filter(goal => goal.name !== 'Investment Target');

  // Add Emergency Fund goal if it doesn't exist
  const hasEmergencyFund = filteredGoals.some(goal => goal.name === 'Emergency Fund');
  if (!hasEmergencyFund) {
    const avgExpenses6months = calculateExpenseAverage(6);
    const avgExpenses3months = calculateExpenseAverage(3);

    filteredGoals.push({
      name: 'Emergency Fund',
      target: avgExpenses6months * 6,
      current: getEmergencyFundCurrent(),
      progress: (avgExpenses6months * 6) ? (getEmergencyFundCurrent() / (avgExpenses6months * 6)) * 100 : 0,
      avgExpenses3months: avgExpenses3months,
      avgExpenses6months: avgExpenses6months,
      isCalculated: true
    });
  }
  // Add Total FIRE Amount goal if it doesn't exist
  const hasFireTotal = filteredGoals.some(goal => goal.name === 'Total Amount Target needed for Fire');
  if (!hasFireTotal) {
    const emergencyGoal = filteredGoals.find(goal => goal.name === 'Emergency Fund');
    const fireGoal = filteredGoals.find(goal => goal.name === 'FIRE Target');

    const emergencyTarget = emergencyGoal?.target || 0;
    const fireTarget = fireGoal?.target || 0;
    const emergencyCurrent = emergencyGoal?.current || 0;

    filteredGoals.push({
      name: 'Total Amount Target needed for Fire',
      target: emergencyTarget + fireTarget,
      current: emergencyCurrent + getFireCurrent(),
      progress: (emergencyTarget + fireTarget) ? ((emergencyCurrent + getFireCurrent()) / (emergencyTarget + fireTarget)) * 100 : 0,
      isCalculated: true
    });
  }

  // ── Balance-sheet view (merged in from the former Net Worth tab) ──
  // autoNetWorth is the same figure as getFireCurrent(): assets − liabilities.
  const autoAssets = totalInvestments + totalCryptoHoldings;
  const autoLiabilities = totalDebts;
  const autoNetWorth = autoAssets - autoLiabilities;

  // ── Glow treatment (spec: chart glow system) ──
  // Progress bars reuse the shared arcGlow filter + resolved v4 tokens (via
  // ChartDefs / useChartTheme / chartTokens) so every data mark carries the
  // signature bloom and dials back in light mode automatically. This tab's
  // signature accent is BLUE (--chart-blue); goals that hit 100% flip to the
  // positive green token as a directional cue.
  const glow = useChartTheme();
  const glowIds = makeChartIds(useId());
  const ACCENT = 'var(--chart-blue)';                                        // tab signature (blue)
  const ACCENT_SOFT = 'color-mix(in srgb, var(--chart-blue) 14%, transparent)';
  const goalFillStyle = (progress) => {
    const tone = progress >= 100 ? 'pos' : 'blue';
    return {
      background: `linear-gradient(180deg, ${lighten(glow.tone[tone], 0.45)}, ${glow.tone[tone]})`,
      filter: `url(#${glowIds.glow})`,
    };
  };

  // Net Worth status → Lucide icon + v4 token tone (no emoji).
  const netWorthStatus =
    autoNetWorth >= 1000000 ? { Icon: Rocket, label: 'Millionaire!', cls: 'bg-arc-pos-soft text-arc-pos' }
    : autoNetWorth >= 500000 ? { Icon: Award, label: 'Half Millionaire', style: { background: ACCENT_SOFT, color: ACCENT } }
    : autoNetWorth >= 100000 ? { Icon: TrendingUp, label: 'Six Figures', cls: 'bg-arc-warn-soft text-arc-warn' }
    : autoNetWorth >= 0 ? { Icon: Scale, label: 'Positive Net Worth', cls: 'bg-arc-paper-2 text-arc-ink' }
    : { Icon: AlertTriangle, label: 'Negative Net Worth', cls: 'bg-arc-neg-soft text-arc-neg' };
  const NetWorthIcon = netWorthStatus.Icon;

  return (
    <div className="space-y-6">
      {/* Shared glow <defs> — one instance provides the arcGlow filter every
          progress bar below references via url(#…). */}
      <ChartDefs ids={glowIds} colors={glow} />

      {/* Header */}
      <div className="flex justify-between items-center">
        <h3 className="text-h2 font-semibold text-arc-ink">Financial Goals</h3>
        <button
          onClick={onToggleEdit}
          className={`flex items-center gap-2 px-3 py-2 rounded-arc-ctl font-semibold shadow-arc-sm transition hover:brightness-105 active:brightness-95 ${
            editMode ? 'bg-arc-pos text-arc-paper' : 'bg-arc-accent text-arc-paper'
          }`}
        >
          {editMode ? <Save size={16} /> : <Edit3 size={16} />} {editMode ? 'Save' : 'Edit'}
        </button>
      </div>

      {/* ── Where You Stand — balance sheet (merged from Net Worth tab) ── */}
      <div className="bg-arc-card p-6 rounded-arc-card shadow-arc-sm ring-1 ring-arc-line">
        <h4 className="text-lg font-semibold mb-4 text-arc-ink">Where You Stand</h4>

        {/* Auto-calculation info panel */}
        <div className="mb-6 p-4 rounded-2xl border border-arc-line bg-arc-paper-2">
          <p className="text-[11px] font-semibold tracking-[0.04em] uppercase text-arc-ink-4 mb-3">
            Auto-Calculated Net Worth
          </p>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-arc-ink-3 text-xs">Manual Investments</p>
              <p className="font-semibold tnum text-arc-ink">{formatCurrency(totalInvestments)}</p>
              <p className="text-[10px] text-arc-ink-4 mt-0.5">From Investments tab</p>
            </div>
            <div>
              <p className="text-arc-ink-3 text-xs">On-Chain Wallets</p>
              <p className="font-semibold tnum text-arc-accent-ink">{formatCurrency(totalCryptoHoldings)}</p>
              <p className="text-[10px] text-arc-ink-4 mt-0.5">From Wallets tab</p>
            </div>
            <div>
              <p className="text-arc-ink-3 text-xs">Total Liabilities</p>
              <p className="font-semibold tnum text-arc-neg">{formatCurrency(autoLiabilities)}</p>
              <p className="text-[10px] text-arc-ink-4 mt-0.5">From Debt tab</p>
            </div>
            <div>
              <p className="text-arc-ink-3 text-xs">Net Worth</p>
              <p
                className="font-semibold text-lg tnum"
                style={{ color: autoNetWorth >= 0 ? ACCENT : 'var(--arc-neg)' }}
              >
                {formatCurrency(autoNetWorth)}
              </p>
              <p className="text-[10px] text-arc-ink-4 mt-0.5">Assets − Liabilities</p>
            </div>
          </div>
        </div>

        {/* Asset / Liability / NW cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          {[
            {
              label: 'Total Assets',
              value: autoAssets,
              cls: 'bg-arc-pos-soft text-arc-pos',
              description: 'Investments + on-chain wallets'
            },
            {
              label: 'Total Liabilities',
              value: autoLiabilities,
              cls: 'bg-arc-neg-soft text-arc-neg',
              description: 'Sum of all current debts'
            },
            {
              label: 'Net Worth',
              value: autoNetWorth,
              style: autoNetWorth >= 0
                ? { background: ACCENT_SOFT, color: ACCENT }
                : undefined,
              cls: autoNetWorth >= 0 ? '' : 'bg-arc-neg-soft text-arc-neg',
              description: 'Assets − Liabilities'
            },
          ].map(({ label, value, cls, style, description }) => (
            <div
              key={label}
              className={`text-center p-4 rounded-arc-card ring-1 ring-arc-line ${cls}`}
              style={style}
            >
              <p className="text-arc-ink-2 text-sm">{label}</p>
              <p className={`font-bold tnum ${label === 'Net Worth' ? 'text-3xl' : 'text-2xl'}`}>
                {formatCurrency(value)}
              </p>
              <p className="text-xs text-arc-ink-3 mt-1">{description}</p>
              <span
                className="inline-block px-2.5 py-1 rounded-arc-sm text-[11px] font-semibold mt-1"
                style={{ background: ACCENT_SOFT, color: ACCENT }}
              >
                Auto-calculated
              </span>
            </div>
          ))}
        </div>

        {/* Net Worth Breakdown */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Assets Breakdown */}
          <div className="p-4 bg-arc-pos-soft rounded-arc-card ring-1 ring-arc-line">
            <h4 className="font-semibold text-arc-pos mb-3">Assets Breakdown</h4>
            <div className="space-y-2 text-sm text-arc-ink-2">
              <div className="flex justify-between">
                <span>Investment Portfolio:</span>
                <span className="font-semibold tnum text-arc-ink">{formatCurrency(totalInvestments)}</span>
              </div>
              <div className="flex justify-between">
                <span>On-Chain Wallets:</span>
                <span className="font-semibold tnum text-arc-ink">{formatCurrency(totalCryptoHoldings)}</span>
              </div>
              <div className="border-t border-arc-line pt-2 flex justify-between font-semibold">
                <span>Total Assets:</span>
                <span className="tnum text-arc-pos">{formatCurrency(autoAssets)}</span>
              </div>
            </div>
            <p className="flex items-center gap-1.5 text-xs text-arc-ink-3 mt-2">
              <Lightbulb size={13} className="text-arc-accent shrink-0" />
              Add more investments in the Investments tab to increase your assets
            </p>
          </div>

          {/* Liabilities Breakdown */}
          <div className="p-4 bg-arc-neg-soft rounded-arc-card ring-1 ring-arc-line">
            <h4 className="font-semibold text-arc-neg mb-3">Liabilities Breakdown</h4>
            <div className="space-y-2 text-sm text-arc-ink-2">
              <div className="flex justify-between">
                <span>Outstanding Debts:</span>
                <span className="font-semibold tnum text-arc-ink">{formatCurrency(totalDebts)}</span>
              </div>
              <div className="border-t border-arc-line pt-2 flex justify-between font-semibold">
                <span>Total Liabilities:</span>
                <span className="tnum text-arc-neg">{formatCurrency(autoLiabilities)}</span>
              </div>
            </div>
            <p className="flex items-center gap-1.5 text-xs text-arc-ink-3 mt-2">
              <Lightbulb size={13} className="text-arc-accent shrink-0" />
              Pay down debts in the Debt tab to reduce your liabilities
            </p>
          </div>
        </div>

        {/* Net Worth Status */}
        <div className="mt-6 p-4 bg-arc-paper-2 rounded-arc-card ring-1 ring-arc-line">
          <h4 className="font-semibold text-arc-ink mb-2">Net Worth Status</h4>
          <div className="flex items-center gap-4">
            <div
              className={`flex items-center gap-2 px-3 py-2 rounded-full font-medium ${netWorthStatus.cls || ''}`}
              style={netWorthStatus.style}
            >
              <NetWorthIcon size={16} />
              <span>{netWorthStatus.label}</span>
            </div>
            <span className="text-sm text-arc-ink-2">
              {autoNetWorth >= 0 ? 'Keep building wealth!' : 'Focus on debt reduction'}
            </span>
          </div>
        </div>
      </div>

      {/* Goal Cards */}
      {filteredGoals.map((goal, idx) => (
        <div key={goal.name} className="bg-arc-card p-6 rounded-arc-card shadow-arc-sm ring-1 ring-arc-line">
          {/* Title + % */}
          <div className="flex justify-between items-center mb-4">
            <h4 className="text-lg font-semibold text-arc-ink">
              {editMode && !goal.isCalculated ? (
                <EditableInput
                  type="text"
                  value={goal.name}
                  onChange={(v) => updateGoal(idx, 'name', v)}
                  className="font-semibold text-lg border-none bg-transparent p-0"
                />
              ) : (
                goal.name
              )}
            </h4>
            <span
              className="text-2xl font-bold tnum"
              style={{ color: goal.progress >= 100 ? 'var(--arc-pos)' : ACCENT }}
            >
              {goal.progress.toFixed(1)}%
            </span>
          </div>

          {/* Progress bar — glow treatment (tab accent blue, arcGlow filter) */}
          <div className="w-full bg-arc-paper-2 rounded-full h-4 mb-4">
            <div
              className="h-4 rounded-full transition-all duration-300"
              style={{
                width: `${Math.min(goal.progress, 100)}%`,
                ...goalFillStyle(goal.progress),
              }}
            />
          </div>

          {/* Special info for Emergency Fund */}
          {goal.name === 'Emergency Fund' && (
            <div className="mb-4 p-3 rounded-arc-card ring-1 ring-arc-line" style={{ background: ACCENT_SOFT }}>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-arc-ink-2">3-Month Average Expenses</p>
                  <p className="font-semibold tnum" style={{ color: ACCENT }}>
                    {formatCurrency(goal.avgExpenses3months)}
                  </p>
                </div>
                <div>
                  <p className="text-arc-ink-2">6-Month Average Expenses</p>
                  <p className="font-semibold tnum" style={{ color: ACCENT }}>
                    {formatCurrency(goal.avgExpenses6months)}
                  </p>
                </div>
              </div>
              <p className="text-xs text-arc-ink-3 mt-2">
                Target: 6 months of expenses • Current: Sum of Emergency Fund investments
              </p>
              {getEmergencyFundCurrent() === 0 && (
                <div className="mt-2 p-2 bg-arc-warn-soft rounded-arc-sm ring-1 ring-arc-line">
                  <p className="flex items-center gap-1.5 text-arc-warn text-xs">
                    <Lightbulb size={13} className="shrink-0" />
                    No investments categorized as "Emergency Fund". Go to Investments tab and set some investments to "Emergency Fund" category.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Special info for FIRE Target */}
          {goal.name === 'FIRE Target' && (
            <div className="mb-4 p-3 rounded-2xl border border-arc-line bg-arc-paper-2">
              <p className="text-xs text-arc-ink-2 mb-3">
                Formula: Manual Investments + On-Chain Wallets − Total Debts
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-arc-ink-3 text-xs">Manual Investments</p>
                  <p className="font-semibold tnum text-arc-ink">{formatCurrency(totalInvestments)}</p>
                </div>
                <div>
                  <p className="text-arc-ink-3 text-xs">On-Chain Wallets</p>
                  <p className="font-semibold tnum text-arc-accent-ink">{formatCurrency(totalCryptoHoldings)}</p>
                </div>
                <div>
                  <p className="text-arc-ink-3 text-xs">Total Debts</p>
                  <p className="font-semibold tnum text-arc-neg">−{formatCurrency(totalDebts)}</p>
                </div>
              </div>
            </div>
          )}

          {/* Special info for Total FIRE Amount */}
          {goal.name === 'Total Amount Target needed for Fire' && (
            <div className="mb-4 p-3 bg-arc-paper-2 rounded-arc-card ring-1 ring-arc-line">
              <p className="text-sm text-arc-ink-2">
                Debt - (Combined Emergency Fund + FIRE Target goals)
              </p>
            </div>
          )}

          {/* Stats grid */}
          <div className="grid grid-cols-3 gap-4 text-center">
            {/* Current */}
            <div>
              <p className="text-arc-ink-3 text-sm">Current</p>
              <p className={`text-lg font-semibold tnum ${goal.current < 0 ? 'text-arc-neg' : 'text-arc-pos'}`}>
                {editMode && !goal.isCalculated && goal.name !== 'FIRE Target' && goal.name !== 'Emergency Fund' ? (
                  <EditableInput
                    value={goal.current}
                    onChange={(v) => updateGoal(idx, 'current', v)}
                    className="w-24 text-center"
                  />
                ) : (
                  formatCurrency(goal.current)
                )}
              </p>
              {(goal.name === 'FIRE Target' || goal.name === 'Emergency Fund') && (
                <p className="text-xs text-arc-ink-4 mt-1">Auto-calculated</p>
              )}
            </div>

            {/* Target */}
            <div>
              <p className="text-arc-ink-3 text-sm">Target</p>
              <p className="text-lg font-semibold tnum" style={{ color: ACCENT }}>
                {editMode && !goal.isCalculated && goal.name !== 'Emergency Fund' ? (
                  <EditableInput
                    value={goal.target}
                    onChange={(v) => updateGoal(idx, 'target', v)}
                    className="w-24 text-center"
                  />
                ) : (
                  formatCurrency(goal.target)
                )}
              </p>
              {goal.name === 'Emergency Fund' && (
                <p className="text-xs text-arc-ink-4 mt-1">6 months expenses</p>
              )}
            </div>

            {/* Remaining */}
            <div>
              <p className="text-arc-ink-3 text-sm">Remaining</p>
              <p className={`text-lg font-semibold tnum ${
                (goal.target - goal.current) <= 0 ? 'text-arc-pos' : 'text-arc-neg'
              }`}>
                {formatCurrency(Math.max(0, goal.target - goal.current))}
              </p>
              {(goal.target - goal.current) <= 0 && (
                <p className="flex items-center justify-center gap-1 text-xs text-arc-pos mt-1">
                  <PartyPopper size={12} /> Goal achieved!
                </p>
              )}
            </div>
          </div>

          {/* Achievement badge */}
          {goal.progress >= 100 && (
            <div className="mt-4 p-3 bg-arc-pos-soft rounded-arc-card ring-1 ring-arc-line">
              <p className="flex items-center justify-center gap-2 text-arc-pos text-sm font-medium text-center">
                <PartyPopper size={16} /> Congratulations! You've achieved this goal!
              </p>
            </div>
          )}
        </div>
      ))}

      {/* Summary Stats */}
      <div className="bg-arc-card p-6 rounded-arc-card shadow-arc-sm ring-1 ring-arc-line">
        <h4 className="text-lg font-semibold mb-4 text-arc-ink">Goals Summary</h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="text-center">
            <p className="text-arc-ink-3 text-sm">Goals Achieved</p>
            <p className="text-2xl font-bold tnum text-arc-pos">
              {filteredGoals.filter(g => g.progress >= 100).length} / {filteredGoals.length}
            </p>
          </div>
          <div className="text-center">
            <p className="text-arc-ink-3 text-sm">Average Progress</p>
            <p className="text-2xl font-bold tnum" style={{ color: ACCENT }}>
              {(filteredGoals.reduce((sum, g) => sum + g.progress, 0) / filteredGoals.length).toFixed(1)}%
            </p>
          </div>
          <div className="text-center">
            <p className="text-arc-ink-3 text-sm">Total Target Amount</p>
            <p className="text-2xl font-bold tnum text-arc-accent-ink">
              {formatCurrency(filteredGoals
                .filter(g => g.name !== 'Total Amount Target needed for Fire')
                .reduce((sum, g) => sum + g.target, 0)
              )}
            </p>
          </div>
        </div>
      </div>

      {/* Apex CTA — the only program CTA on this surface. Everyone in the
          FIRE app is already a Galaxy member, so the upsell is Galaxy → Apex.
          Gated at six-figure net worth = qualified-lead threshold. */}
      {autoNetWorth >= 100000 && (
        <div className="bg-arc-card p-6 rounded-arc-card shadow-arc-sm ring-1 ring-arc-line mt-6">
          <p className="text-[11px] font-semibold tracking-[0.04em] uppercase text-arc-ink-4 mb-1">
            You've built a real foundation
          </p>
          <p className="text-sm text-arc-ink-2 mb-4">
            Six figures+ is the threshold where strategy starts to matter more
            than savings rate. Talk with an ARC strategist about what students
            at this level commonly focus on next.
          </p>
          <AffiliateCard
            link={ARC_PRODUCTS.apexone}
            medium="goals-tab-apex"
            featured
          />
        </div>
      )}
    </div>
  );
};

export default GoalsTab;
